import React from "react";
import { Circle, Document, Page, Path, Rect, StyleSheet, Svg, Text, View } from "@react-pdf/renderer";
import { commonStyles, formatPhoneNumber } from "../../Services/ReactPDFService.jsx";

// Professional Blue Cover Letter Template - Elegant design with centered name and blue accents
const styles = StyleSheet.create({
  ...commonStyles,
  page: {
    ...commonStyles.page,
    fontFamily: "Roboto",
    backgroundColor: "#ffffff",
    padding: 0,
  },
  // Header section with white background
  headerContainer: {
    backgroundColor: "#2c3e50",
    paddingHorizontal: 45,
    paddingTop: 35,
    paddingBottom: 25,
    borderBottomWidth: 2,
    borderBottomColor: "#2c3e50",
  },
  // Name styling - centered and elegant
  name: {
    fontSize: 28,
    fontWeight: 400,
    color: "#ffffff",
    textAlign: "center",
    letterSpacing: 0.5,
    marginBottom: 25,
  },
  // Contact info row - centered with icons
  contactRow: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    flexWrap: "wrap",
    gap: 20,
  },
  contactItem: {
    flexDirection: "row",
    alignItems: "center",
    gap: 5,
  },
  contactIcon: {
    width: 12,
    height: 12,
  },
  contactText: {
    fontSize: 10,
    color: "#ffffff",
  },
  // Main content section with side decorations
  mainContentSection: {
    flexDirection: "row",
    flex: 1,
  },
  // Left decoration column
  leftDecorColumn: {
    width: 25,
    paddingTop: 30,
    alignItems: "center",
  },
  // Right decoration column
  rightDecorColumn: {
    width: 25,
    paddingTop: 30,
    alignItems: "center",
  },
  // Main content area
  contentWrapper: {
    flex: 1,
    paddingVertical: 30,
    paddingHorizontal: 20,
  },
  // Body text styles
  bodyText: {
    fontSize: 11,
    lineHeight: 1.7,
    color: "#444444",
    textAlign: "justify",
    marginBottom: 14,
  },
  // Bottom decoration row
  bottomDecorRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    paddingHorizontal: 30,
    paddingBottom: 20,
  },
});

const ProfessionalBlueCoverLetter = ({ coverLetterData }) => {
  const { header, generatedContent } = coverLetterData || {};
  const { name, email, phone, location } = header || {};

  // Parse content into paragraphs
  const paragraphs = generatedContent
    ? generatedContent.split("\n\n").filter((p) => p.trim())
    : ["No content generated yet."];

  return (
    <Document>
      <Page size="A4" style={styles.page}>
        {/* Header Section */}
        <View style={styles.headerContainer}>
          {/* Name - centered and elegant */}
          <Text style={styles.name}>{name || "Your Name"}</Text>

          {/* Contact info row with icons */}
          <View style={styles.contactRow}>
            {/* Location */}
            {location && (
              <View style={styles.contactItem}>
                <Svg style={styles.contactIcon} viewBox="0 0 24 24">
                  <Path
                    d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"
                    fill="#ffffff"
                  />
                </Svg>
                <Text style={styles.contactText}>{location}</Text>
              </View>
            )}

            {/* Email */}
            {email && (
              <View style={styles.contactItem}>
                <Svg style={styles.contactIcon} viewBox="0 0 24 24">
                  <Path
                    d="M20 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z"
                    fill="#ffffff"
                  />
                </Svg>
                <Text style={styles.contactText}>{email}</Text>
              </View>
            )}

            {/* Phone */}
            {phone && (
              <View style={styles.contactItem}>
                <Svg style={styles.contactIcon} viewBox="0 0 24 24">
                  <Path
                    d="M6.62 10.79c1.44 2.83 3.76 5.14 6.59 6.59l2.2-2.2c.27-.27.67-.36 1.02-.24 1.12.37 2.33.57 3.57.57.55 0 1 .45 1 1V20c0 .55-.45 1-1 1-9.39 0-17-7.61-17-17 0-.55.45-1 1-1h3.5c.55 0 1 .45 1 1 0 1.25.2 2.45.57 3.57.11.35.03.74-.25 1.02l-2.2 2.2z"
                    fill="#ffffff"
                  />
                </Svg>
                <Text style={styles.contactText}>{formatPhoneNumber(phone)}</Text>
              </View>
            )}
          </View>
        </View>

        {/* Main Content Section with Side Decorations */}
        <View style={styles.mainContentSection}>
          {/* Left Side Decorations */}
          <View style={styles.leftDecorColumn}>
            {/* Vertical dots pattern */}
            <Svg width="20" height="120" viewBox="0 0 20 120">
              <Circle cx="10" cy="10" r="4" fill="#3b5998" opacity="0.12" />
              <Circle cx="10" cy="30" r="3" fill="#3b5998" opacity="0.10" />
              <Circle cx="10" cy="48" r="4" fill="#3b5998" opacity="0.08" />
              <Circle cx="10" cy="66" r="3" fill="#3b5998" opacity="0.12" />
              <Circle cx="10" cy="84" r="4" fill="#3b5998" opacity="0.10" />
              <Circle cx="10" cy="102" r="3" fill="#3b5998" opacity="0.08" />
              <Circle cx="10" cy="118" r="2" fill="#3b5998" opacity="0.06" />
            </Svg>

            {/* Diamond shape */}
            <Svg width="18" height="18" viewBox="0 0 18 18" style={{ marginTop: 20 }}>
              <Path d="M9 0 L18 9 L9 18 L0 9 Z" fill="#3b5998" opacity="0.10" />
            </Svg>

            {/* Vertical line */}
            <Svg width="4" height="80" viewBox="0 0 4 80" style={{ marginTop: 15 }}>
              <Rect x="0" y="0" width="2" height="25" fill="#3b5998" opacity="0.12" />
              <Rect x="0" y="30" width="2" height="15" fill="#3b5998" opacity="0.08" />
              <Rect x="0" y="50" width="2" height="30" fill="#3b5998" opacity="0.10" />
            </Svg>

            {/* Small circles */}
            <Svg width="15" height="60" viewBox="0 0 15 60" style={{ marginTop: 20 }}>
              <Circle cx="7" cy="10" r="5" fill="none" stroke="#3b5998" strokeWidth="1" opacity="0.15" />
              <Circle cx="7" cy="35" r="3" fill="#3b5998" opacity="0.10" />
              <Circle cx="7" cy="55" r="4" fill="#3b5998" opacity="0.08" />
            </Svg>
          </View>

          {/* Main Content */}
          <View style={styles.contentWrapper}>
            {/* Body paragraphs */}
            {paragraphs.map((paragraph, index) => (
              <Text key={index} style={styles.bodyText}>
                {paragraph.trim()}
              </Text>
            ))}
          </View>

          {/* Right Side Decorations */}
          <View style={styles.rightDecorColumn}>
            {/* Vertical dots pattern */}
            <Svg width="20" height="120" viewBox="0 0 20 120">
              <Circle cx="10" cy="10" r="4" fill="#3b5998" opacity="0.12" />
              <Circle cx="10" cy="30" r="3" fill="#3b5998" opacity="0.10" />
              <Circle cx="10" cy="48" r="4" fill="#3b5998" opacity="0.08" />
              <Circle cx="10" cy="66" r="3" fill="#3b5998" opacity="0.12" />
              <Circle cx="10" cy="84" r="4" fill="#3b5998" opacity="0.10" />
              <Circle cx="10" cy="102" r="3" fill="#3b5998" opacity="0.08" />
              <Circle cx="10" cy="118" r="2" fill="#3b5998" opacity="0.06" />
            </Svg>

            {/* Diamond shape */}
            <Svg width="18" height="18" viewBox="0 0 18 18" style={{ marginTop: 20 }}>
              <Path d="M9 0 L18 9 L9 18 L0 9 Z" fill="#3b5998" opacity="0.10" />
            </Svg>

            {/* Vertical line */}
            <Svg width="4" height="80" viewBox="0 0 4 80" style={{ marginTop: 15 }}>
              <Rect x="2" y="0" width="2" height="25" fill="#3b5998" opacity="0.12" />
              <Rect x="2" y="30" width="2" height="15" fill="#3b5998" opacity="0.08" />
              <Rect x="2" y="50" width="2" height="30" fill="#3b5998" opacity="0.10" />
            </Svg>

            {/* Small circles */}
            <Svg width="15" height="60" viewBox="0 0 15 60" style={{ marginTop: 20 }}>
              <Circle cx="7" cy="10" r="5" fill="none" stroke="#3b5998" strokeWidth="1" opacity="0.15" />
              <Circle cx="7" cy="35" r="3" fill="#3b5998" opacity="0.10" />
              <Circle cx="7" cy="55" r="4" fill="#3b5998" opacity="0.08" />
            </Svg>
          </View>
        </View>

        {/* Bottom Decorations */}
        <View style={styles.bottomDecorRow}>
          {/* Left bottom decorations */}
          <Svg width="100" height="20" viewBox="0 0 100 20">
            <Circle cx="10" cy="10" r="6" fill="#3b5998" opacity="0.10" />
            <Circle cx="30" cy="10" r="4" fill="#3b5998" opacity="0.08" />
            <Rect x="45" y="8" width="20" height="4" fill="#3b5998" opacity="0.10" />
            <Rect x="70" y="8" width="12" height="4" fill="#3b5998" opacity="0.06" />
            <Circle cx="92" cy="10" r="3" fill="#3b5998" opacity="0.08" />
          </Svg>

          {/* Center hexagon */}
          <Svg width="24" height="22" viewBox="0 0 24 22">
            <Path
              d="M12 0 L22 5.5 L22 16.5 L12 22 L2 16.5 L2 5.5 Z"
              fill="none"
              stroke="#3b5998"
              strokeWidth="1"
              opacity="0.15"
            />
          </Svg>

          {/* Right bottom decorations */}
          <Svg width="100" height="20" viewBox="0 0 100 20">
            <Circle cx="90" cy="10" r="6" fill="#3b5998" opacity="0.10" />
            <Circle cx="70" cy="10" r="4" fill="#3b5998" opacity="0.08" />
            <Rect x="35" y="8" width="20" height="4" fill="#3b5998" opacity="0.10" />
            <Rect x="18" y="8" width="12" height="4" fill="#3b5998" opacity="0.06" />
            <Circle cx="8" cy="10" r="3" fill="#3b5998" opacity="0.08" />
          </Svg>
        </View>
      </Page>
    </Document>
  );
};

export default ProfessionalBlueCoverLetter;
